/** Erorile interfeței în Sentry, cu aceleași reguli ca serviciile Python
 *  (`observare/README.md`): același proiect, mediu, versiune, ce nu pleacă
 *  din platformă și cât de des pleacă aceeași eroare.
 *
 *  Ce se prinde fără nicio linie în ecrane:
 *
 *  * o componentă care aruncă la randare: plasa de la rădăcină
 *    (`components/InterfataCazuta.tsx`) o trimite cu stiva de componente și
 *    arată un ecran în locul paginii albe;
 *  * o excepție nerezolvată și o promisiune respinsă pe care n-a prins-o
 *    nimeni (`window.onerror`, `unhandledrejection`): codul din handlere, din
 *    bucla de randare 3D, din workere aduse în fir.
 *
 *  Fără `VITE_SENTRY_DSN` la build nu pornește nimic: `npm run dev`, testele și
 *  CI-ul nu trimit nimic nicăieri. DSN-ul, mediul și versiunea intră în bundle
 *  la build (`ui/Dockerfile`), fiindcă imaginea UI-ului se construiește pe
 *  fiecare mediu din `.env`-ul lui.
 */
import * as Sentry from "@sentry/react";
import type { Breadcrumb, ErrorEvent, EventHint } from "@sentry/react";

/** Eticheta `serviciu` a evenimentelor; în Python e cheia din compose. */
export const SERVICIU = "ui";

/** Aceeași eroare pleacă din aceeași pagină cel mult o dată la atâtea ms.
 *  Bucla de randare 3D aruncă la fiecare cadru: 60 de evenimente pe secundă
 *  de la un singur om, iar `dedupe` din SDK oprește numai repetarea IMEDIATĂ,
 *  nu și două erori care se întrepătrund. Cinci minute, ca pe server. */
export const FEREASTRA_REPETARI_MS = 300_000;

export function porneste(): boolean {
  const dsn = (import.meta.env.VITE_SENTRY_DSN ?? "").trim();
  if (!dsn) return false;
  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_OPTIMUS_ENV || undefined,
    release: import.meta.env.VITE_DEPLOYED_REF || undefined,
    // FĂRĂ IP, cookie-uri și utilizator, ca pe server: sesiunea omului nu are
    // ce căuta la un terț. Implicitele SDK-ului v11 le strâng pe toate, iar
    // `userInfo` oprește și IP-ul dedus de Sentry la primire (`infer_ip`).
    // Antetele rămân: în browser sunt numai User-Agent (ce browser a căzut) și
    // Referer, curățat mai jos. Fără Replay (ar filma documentele clientului)
    // și fără urme de performanță: aici se raportează erorile.
    dataCollection: { userInfo: false, cookies: false, httpBodies: [] },
    initialScope: { tags: { serviciu: SERVICIU } },
    // Numai codul nostru: o extensie a browserului care aruncă în pagină nu
    // e o eroare a platformei. Bundle-ul și chunk-urile lui stau pe același
    // origin; o eroare fără stivă trece oricum (n-are după ce se filtra).
    allowUrls: [window.location.origin],
    ignoreErrors: [
      // Avertismentul browserului când un ResizeObserver nu prinde un cadru;
      // nu strică nimic, iar canvasurile 3D îl declanșează la fiecare
      // redimensionare a panourilor.
      /ResizeObserver loop/,
    ],
    beforeSend: inainteDeTrimitere,
    beforeBreadcrumb: inainteDeUrma,
  });
  return true;
}

// ---------------------------------------------------------------------------
// Ce nu pleacă din platformă
// ---------------------------------------------------------------------------

// Aceleași tipare ca `observare/__init__.py`. Adresa paginii și cererile spre
// `/api` poartă tokenul invitației (`/invitation/<token>`) și adresa scrisă de
// om în căutare (`/api/geo/search-address?q=`).
const EMAIL = /(?<![\w.+-])[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;
const URL_ABSOLUT = /https?:\/\/[^\s'"<>]+/g;
// Orice valoare dintr-o interogare: `?q=` e adresa pe care a scris-o omul,
// `?key=` cheia hărții. În browser interogarea stă în aceeași adresă cu calea.
const VALOARE_INTEROGARE = /((?:^|[?&])[^=&#\s]+=)([^&#\s]*)/g;
// Un segment de cale lung și aleator e un token, nu un id; uuid-urile rămân,
// fiindcă ele spun pe ce proiect și pe ce soluție a căzut ecranul.
const SEGMENT_LUNG = /(?<=\/)[A-Za-z0-9_-]{24,}(?=[/?#]|$)/g;
const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** Un șir care E o adresă, absolută sau relativă (`/api/...`, o rută). */
export function ascundeAdresa(u: string): string {
  const q = u.indexOf("?");
  const cale = q < 0 ? u : u.slice(0, q);
  const interogare = q < 0 ? "" : u.slice(q);
  return cale.replace(SEGMENT_LUNG, (s) => (UUID.test(s) ? s : "<ascuns>"))
    + ascundeInterogarea(interogare);
}

/** Valorile interogării, afară de uuid-uri (`?pe=<soluția>` spune pe ce a
 *  căzut ecranul). */
function ascundeInterogarea(text: string): string {
  return text.replace(VALOARE_INTEROGARE,
    (_, nume: string, v: string) => nume + (UUID.test(v) || v === "" ? v : "<ascuns>"));
}

/** Un text liber: mesajul excepției, o linie din consolă. */
export function ascunde<T>(text: T): T {
  if (typeof text !== "string" || !text) return text;
  return text.replace(URL_ABSOLUT, ascundeAdresa).replace(EMAIL, "<email>") as T;
}

function ascundeInUrma(urma: Breadcrumb): Breadcrumb {
  urma.message = ascunde(urma.message);
  const date = urma.data;
  if (date) {
    for (const k of Object.keys(date)) {
      const v = date[k];
      // `url` la fetch/xhr, `from`/`to` la navigare: adrese relative, pe care
      // tiparul de URL absolut nu le-ar vedea.
      if (typeof v === "string") date[k] = ["url", "from", "to"].includes(k) ? ascundeAdresa(v) : ascunde(v);
      else if (k === "arguments" && Array.isArray(v)) date[k] = v.map((x) => ascunde(x));
    }
    if (typeof date["http.query"] === "string") date["http.query"] = ascundeInterogarea(date["http.query"]);
  }
  return urma;
}

export function ascundeInEveniment(event: ErrorEvent): void {
  if (event.message) event.message = ascunde(event.message);
  for (const v of event.exception?.values ?? []) v.value = ascunde(v.value);
  for (const u of event.breadcrumbs ?? []) ascundeInUrma(u);
  const cerere = event.request;
  if (cerere) {
    if (cerere.url) cerere.url = ascundeAdresa(cerere.url);
    // Referer-ul unei pagini de invitație poartă tokenul în cale
    for (const k of Object.keys(cerere.headers ?? {})) {
      cerere.headers![k] = ascunde(cerere.headers![k]);
    }
  }
}

// ---------------------------------------------------------------------------
// Rărirea
// ---------------------------------------------------------------------------

const vazute = new Map<string, { la: number; oprite: number }>();

/** «Aceeași eroare»: același tip și același drum prin cod. Fără stivă (o
 *  promisiune respinsă cu un text), textul cu numerele și id-urile scoase. */
export function cheieEveniment(event: ErrorEvent): string {
  const parti: string[] = [];
  for (const v of event.exception?.values ?? []) {
    parti.push(v.type ?? "");
    for (const c of v.stacktrace?.frames ?? []) parti.push(`${c.filename}:${c.lineno}:${c.colno}`);
    if (!v.stacktrace?.frames?.length) parti.push(normalizeaza(v.value ?? ""));
  }
  if (!parti.length) parti.push(normalizeaza(event.message ?? ""));
  return parti.join("|");
}

function normalizeaza(text: string): string {
  return text
    .replace(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g, "U")
    .replace(/\b[0-9a-fA-F]{12,}\b/g, "H")
    .replace(/\d+/g, "N")
    .slice(0, 500);
}

/** Trece sau nu; când trece după o pauză, câte repetări au fost oprite. */
export function lasa(cheie: string, acum: number): [boolean, number] {
  const v = vazute.get(cheie);
  if (v && acum - v.la < FEREASTRA_REPETARI_MS) {
    v.oprite += 1;
    return [false, 0];
  }
  vazute.set(cheie, { la: acum, oprite: 0 });
  return [true, v?.oprite ?? 0];
}

export function inainteDeTrimitere(event: ErrorEvent, _hint?: EventHint): ErrorEvent | null {
  try {
    ascundeInEveniment(event);
    const [trece, oprite] = lasa(cheieEveniment(event), Date.now());
    if (!trece) return null;
    if (oprite) event.extra = { ...event.extra, repetari_netrimise: oprite };
    // Același proiect Sentry ca serviciile: fără serviciu în amprentă, o
    // eroare din UI și una din server cu același text ar fi o singură problemă.
    event.fingerprint = [...(event.fingerprint ?? ["{{ default }}"]), `serviciu:${SERVICIU}`];
  } catch {
    // Un eveniment pe care curățarea n-a putut să-l treacă nu pleacă.
    return null;
  }
  return event;
}

export function inainteDeUrma(urma: Breadcrumb): Breadcrumb | null {
  try {
    return ascundeInUrma(urma);
  } catch {
    return null;
  }
}
